<template>
  <div class="php-panel">

    <!-- Carregando -->
    <div v-if="loading" class="php-state">
      <div class="spinner-border" style="width:3rem;height:3rem;" role="status"></div>
      <p class="mt-3">Carregando painel...</p>
    </div>

    <!-- Não encontrado -->
    <div v-else-if="!dados" class="php-state">
      <i class="fa fa-times-circle fa-3x mb-3"></i>
      <h4>Painel não disponível</h4>
      <p>Este evento não existe ou ainda não tem formulário de contribuições configurado.</p>
    </div>

    <!-- Painel -->
    <div v-else class="php-grid">

      <!-- L1: logos -->
      <div class="php-logos">
        <img
          v-for="(logo, i) in dados.logo_urls"
          :key="'logo' + i"
          :src="logo"
          class="php-logo-img"
          :style="logoStyle(logo)"
          alt="Logo"
          @load="onLogoLoad(logo, $event)"
          @error="(e) => (e.target.style.display = 'none')"
        />
        <span v-if="!dados.logo_urls || !dados.logo_urls.length" class="php-logos-fallback">
          {{ dados.conta_name }}
        </span>
      </div>

      <!-- L2: banner -->
      <div class="php-banner">
        <!-- Arte de linha: silhueta urbana com arborização -->
        <svg class="php-skyline" viewBox="0 0 210 112" fill="none" aria-hidden="true">
          <g stroke="#ffffff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" opacity="0.92">
            <path d="M62 30v-6M59 27h6" />
            <path d="M52 78V38l10-8 10 8v40" />
            <path d="M58 78V58h8v20" />
            <path d="M84 78V26h26v52" />
            <path d="M90 36h5M99 36h5M90 46h5M99 46h5M90 56h5M99 56h5" />
            <path d="M118 78V44h20v34" />
            <path d="M124 52h3M131 52h3M124 62h3M131 62h3" />
            <path d="M28 78V50h16v28" />
            <path d="M33 58h3M39 58h3M33 68h3M39 68h3" />
            <path d="M154 78V40" />
            <path d="M154 44c-7-6-13-4-13-4s3 7 13 4zM154 44c7-6 13-4 13-4s-3 7-13 4zM154 54c-7-6-13-4-13-4s3 7 13 4zM154 54c7-6 13-4 13-4s-3 7-13 4z" />
            <path d="M180 78V58" />
            <circle cx="180" cy="50" r="9" />
            <path d="M16 78V62" />
            <circle cx="16" cy="55" r="8" />
            <path d="M6 90c8 0 8-4 16-4s8 4 16 4 8-4 16-4 8 4 16 4 8-4 16-4 8 4 16 4 8-4 16-4 8 4 16 4 8-4 16-4 8 4 16 4" />
            <path d="M6 100c8 0 8-4 16-4s8 4 16 4 8-4 16-4 8 4 16 4 8-4 16-4 8 4 16 4 8-4 16-4 8 4 16 4 8-4 16-4 8 4 16 4" />
          </g>
        </svg>

        <div class="php-banner-text">
          <h1 class="php-banner-title">Audiência Pública</h1>
          <p class="php-banner-sub">{{ subtituloBanner }}</p>
        </div>

        <div class="php-banner-date">
          <i class="fa fa-calendar-alt php-date-icon"></i>
          <div>
            <div class="php-date-label">Data da audiência</div>
            <div class="php-date-value">{{ dataEvento }}</div>
          </div>
        </div>
      </div>

      <!-- L3: cards de números (mesmas colunas da L4/L5: esquerda = gráfico, direita = QR) -->
      <div class="php-row-cards">
        <div class="php-row-cards-left">
          <div class="php-card">
            <div class="php-card-title php-t-green">Número de contribuições</div>
            <div class="php-kpi-row">
              <div class="php-icon-circle php-bg-green"><i class="fa fa-file-alt"></i></div>
              <div class="php-kpi-group">
                <div class="php-kpi-num php-t-navy">{{ dados.contributions.total }}</div>
                <div class="php-kpi-label">Total</div>
              </div>
              <div class="php-kpi-div"></div>
              <div class="php-kpi-group">
                <div class="php-kpi-num php-t-green">{{ dados.contributions.atendidas }}</div>
                <div class="php-kpi-label">Atendidas</div>
              </div>
              <div class="php-kpi-div"></div>
              <div class="php-kpi-group">
                <div class="php-kpi-num php-t-red">{{ dados.contributions.nao_atendidas }}</div>
                <div class="php-kpi-label">Não<br />atendidas</div>
              </div>
            </div>
          </div>
  
          <div class="php-card">
            <div class="php-card-title php-t-navy">Solicitações de fala</div>
            <div class="php-kpi-row">
              <div class="php-icon-circle php-bg-blue"><i class="fa fa-microphone"></i></div>
              <div class="php-kpi-group">
                <div class="php-kpi-num php-t-navy">{{ dados.speech_requests.total }}</div>
                <div class="php-kpi-label">Total</div>
              </div>
              <div class="php-kpi-div"></div>
              <div class="php-kpi-group">
                <div class="php-kpi-num php-t-green">{{ dados.speech_requests.atendidas }}</div>
                <div class="php-kpi-label">Atendidas</div>
              </div>
              <div class="php-kpi-div"></div>
              <div class="php-kpi-group">
                <div class="php-kpi-num php-t-red">{{ dados.speech_requests.nao_atendidas }}</div>
                <div class="php-kpi-label">Não<br />atendidas</div>
              </div>
            </div>
          </div>
        </div>

        <div class="php-card">
          <template v-if="!dados.active_speaker">
            <div class="php-card-title php-t-purple">Tempo de fala</div>
            <div class="php-kpi-row php-kpi-row-center">
              <div class="php-icon-circle php-bg-purple"><i class="fa fa-clock"></i></div>
              <div class="php-kpi-group">
                <div class="php-kpi-num php-t-purple">{{ tempoFala }}</div>
                <div class="php-kpi-label">Minutos por participante</div>
              </div>
            </div>
          </template>
          <!-- Mesma estrutura (e altura) do estado normal: iniciar/encerrar uma fala não
               pode mudar a altura da linha, senão o bloco de baixo encolhe e o QR some. -->
          <template v-else>
            <div class="php-card-title php-t-purple">Falando agora</div>
            <div class="php-kpi-row php-kpi-row-center">
              <div class="php-icon-circle php-bg-purple">
                <i :class="['fa', dados.active_speaker.paused ? 'fa-pause' : 'fa-microphone']"></i>
              </div>
              <div class="php-kpi-group">
                <div class="php-speaker-name" :title="dados.active_speaker.name">
                  {{ dados.active_speaker.name }}
                </div>
                <div
                  class="php-kpi-num php-t-purple php-countdown"
                  :class="{
                    'php-countdown-ended': remainingSeconds <= 0,
                    'php-countdown-paused': dados.active_speaker.paused
                  }"
                  :title="dados.active_speaker.paused ? 'Fala pausada' : ''"
                >
                  {{ formatarContagem(remainingSeconds) }}
                </div>
              </div>
              <div v-if="isLoggedIn" class="php-timer-actions">
                <button
                  class="php-btn-pausar"
                  :disabled="acaoCronometro"
                  @click="alternarPausaCronometro"
                >
                  {{ dados.active_speaker.paused ? "Retomar" : "Pausar" }}
                </button>
                <button
                  class="php-btn-encerrar"
                  :disabled="acaoCronometro"
                  @click="encerrarCronometro"
                >
                  Encerrar
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- L4/L5: gráfico e fila à esquerda, QR ocupando a coluna direita inteira -->
      <div class="php-row-main">
        <div class="php-card php-card-chart">
          <div class="php-card-title php-t-navy">Número de contribuições por capítulo do PMAU</div>
          <div ref="chartBox" class="php-chart-box">
            <highcharts v-if="chartOptions" :options="chartOptions" />
          </div>
        </div>

        <div class="php-card php-card-qr">
          <div class="php-qr-title">Quer participar?</div>
          <div class="php-qr-sub">Faça sua inscrição para fala ou contribuição</div>
          <div class="php-qr-area">
            <div class="php-qr-frame">
              <img v-if="qrUrl" :src="qrUrl" class="php-qr-img" alt="QR Code do formulário" />
              <i v-else class="fa fa-qrcode php-qr-placeholder"></i>
            </div>
          </div>
          <div class="php-qr-btn">Escaneie o QR Code</div>
          <div class="php-qr-caption">E acesse o formulário de inscrição</div>
        </div>

        <!-- fila de solicitações de fala -->
        <div class="php-card php-card-list">
          <div class="php-card-title php-t-navy">Fila de solicitações de fala</div>
          <div class="php-list">
            <div v-if="!dados.speech_queue.length" class="php-empty">
              Nenhuma solicitação de fala até o momento.
            </div>
            <div
              v-for="(f, i) in dados.speech_queue"
              :key="'f' + i"
              class="php-item"
              :class="{
                'php-item-clickable': isLoggedIn && !ehOradorAtivo(f),
                'php-item-selected': speakerSelecionado === f.id,
                'php-item-active': ehOradorAtivo(f),
                'php-item-spoken': f.spoken && !ehOradorAtivo(f)
              }"
              @click="isLoggedIn && selecionarSolicitante(f)"
            >
              <span class="php-item-pos">{{ i + 1 }}</span>
              <div class="php-item-body">
                <div class="php-item-name">{{ f.name }}</div>
                <div class="php-item-meta">
                  <span v-if="f.institution">{{ f.institution }} · </span>{{ f.chapter }}
                </div>
              </div>
              <button
                v-if="isLoggedIn && speakerSelecionado === f.id && !ehOradorAtivo(f)"
                class="php-btn-iniciar"
                :disabled="acaoCronometro"
                @click.stop="iniciarCronometro(f)"
              >
                <i class="fa fa-play mr-1"></i>Iniciar
              </button>
              <span v-else-if="ehOradorAtivo(f)" class="php-badge php-badge-live">
                <i class="fa fa-microphone mr-1"></i>Falando agora
              </span>
              <!-- Só marcação de controle: quem já falou pode ser iniciado de novo. -->
              <span v-else-if="f.spoken" class="php-badge php-badge-spoken">
                <i class="fa fa-check mr-1"></i>Falou
              </span>
              <span v-else class="php-badge" :class="badgeClass(f.status)">{{ statusLabel(f.status) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- L6: faixa final -->
      <div class="php-footer">
        <i class="fa fa-info-circle php-footer-icon"></i>
        <span>Sua contribuição é fundamental para construirmos juntos o futuro de {{ dados.conta_name }}</span>
        <i class="fa fa-users php-footer-people"></i>
      </div>
    </div>
  </div>
</template>

<script>
import "@fontsource/cabin/400.css";
import "@fontsource/cabin/500.css";
import "@fontsource/cabin/600.css";
import "@fontsource/cabin/700.css";

import PublicHearingService from "../../servico/PublicHearingService";

const POLL_INTERVAL_MS = 3000;
const PANEL_FONT = 'Cabin, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
const NAVY = "#16265C";
const CHART_BAR = "#1B3A6B";

// Nomes curtos por capítulo — os rótulos completos do PMAU não cabem no eixo X
// com 14 colunas. A ordem é a mesma das opções cadastradas (1..14).
const CAPITULOS_CURTOS = {
  1: "Introdução",
  2: "Caracterização",
  3: "Diagnóstico",
  4: "Inventário",
  5: "Planejamento",
  6: "Implantação",
  7: "Manutenção",
  8: "Risco de Queda",
  9: "Monitoramento",
  10: "Gestão",
  11: "Tombamento",
  12: "Workshop",
  13: "Eixos",
  14: "Outros"
};

export default {
  name: "PublicHearingPanel",
  props: {
    // Usado apenas para captura automática do print (ver PmauPublicHearingSnapshot.js) —
    // fora desse fluxo, o token vem sempre da rota pública.
    tokenOverride: { type: String, default: null }
  },
  data() {
    return {
      loading: true,
      dados: null,
      qrUrl: null,
      // proporção (largura/altura) de cada logo, por URL — usada em logoStyle
      logoRatios: {},
      chartH: 240,
      windowWidth: typeof window !== "undefined" ? window.innerWidth : 1920,
      pollTimer: null,
      isLoggedIn: false,
      speakerSelecionado: null,
      remainingSeconds: 0,
      countdownTimer: null,
      acaoCronometro: false
    };
  },
  created() {
    try {
      const local = JSON.parse(localStorage.getItem("user-token") || "null");
      this.isLoggedIn = !!(local && local.token);
    } catch (e) {
      this.isLoggedIn = false;
    }
  },
  computed: {
    token() {
      return this.tokenOverride || this.$route.params.token;
    },
    subtituloBanner() {
      if (!this.dados) return "";
      const cidade = (this.dados.conta_name || "")
        .replace("Prefeitura Municipal de ", "")
        .replace("Prefeitura de ", "")
        .trim();
      return cidade
        ? "Plano Municipal de Arborização Urbana de " + cidade + " (PMAU)"
        : "Plano Municipal de Arborização Urbana (PMAU)";
    },
    dataEvento() {
      if (!this.dados || !this.dados.event.event_date) return "";
      return new Date(this.dados.event.event_date).toLocaleDateString("pt-BR");
    },
    tempoFala() {
      const min = this.dados && this.dados.event.speaking_time_minutes;
      if (min == null) return "—";
      return String(min).padStart(2, "0") + ":00";
    },
    chartOptions() {
      if (!this.dados) return null;
      const chapters = this.dados.by_chapter || [];
      const estreito = this.windowWidth < 1500;
      return {
        chart: {
          type: "column",
          height: this.chartH,
          backgroundColor: "transparent",
          style: { fontFamily: PANEL_FONT },
          spacing: [8, 4, 8, 4],
          // Painel de telão recarrega a cada poucos segundos — animar a cada ciclo pisca.
          animation: false
        },
        title: { text: null },
        credits: { enabled: false },
        legend: { enabled: false },
        xAxis: {
          // No telão (tela larga) cabem os nomes; abaixo disso 14 nomes se
          // sobrepõem, então fica só a numeração do capítulo.
          categories: chapters.map((c) =>
            estreito ? String(c.order) : c.order + ". " + (CAPITULOS_CURTOS[c.order] || c.label)
          ),
          labels: {
            // Sem rotação: o rótulo quebra em linhas, como no layout aprovado.
            autoRotation: [0],
            style: {
              fontSize: estreito ? "13px" : "10px",
              fontWeight: estreito ? "700" : "500",
              color: "#4A5568",
              width: (estreito ? 40 : 84) + "px",
              // Sem isso o Highcharts corta com "..." em vez de quebrar linha.
              textOverflow: "none"
            }
          },
          lineColor: "#CBD5E0",
          tickLength: 0
        },
        yAxis: {
          min: 0,
          maxPadding: 0.16,
          allowDecimals: false,
          title: { text: null },
          gridLineColor: "#EDF1F5",
          labels: { style: { fontSize: "12px", fontWeight: "500", color: "#8A94A6" } }
        },
        tooltip: {
          formatter() {
            return "<b>" + this.point.fullLabel + "</b><br/>" + this.y + " contribuição(ões)";
          }
        },
        plotOptions: {
          series: { animation: false },
          column: {
            borderRadius: 2,
            color: CHART_BAR,
            maxPointWidth: 46,
            dataLabels: {
              enabled: true,
              crop: false,
              overflow: "allow",
              formatter() {
                return this.y > 0 ? this.y : null;
              },
              style: { fontSize: "15px", fontWeight: "700", textOutline: "none", color: NAVY }
            }
          }
        },
        series: [
          {
            name: "Contribuições",
            data: chapters.map((c) => ({ y: c.count, fullLabel: c.label }))
          }
        ]
      };
    }
  },
  beforeMount() {
    this.carregar(true);
  },
  mounted() {
    this._onResize = () => {
      this.windowWidth = window.innerWidth;
      this.$nextTick(this.medirGrafico);
    };
    window.addEventListener("resize", this._onResize);
    this.pollTimer = setInterval(() => this.carregar(false), POLL_INTERVAL_MS);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this._onResize);
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    if (this.qrUrl) URL.revokeObjectURL(this.qrUrl);
  },
  methods: {
    onLogoLoad(url, e) {
      const img = e.target;
      if (img.naturalWidth && img.naturalHeight) {
        this.$set(this.logoRatios, url, img.naturalWidth / img.naturalHeight);
      }
    },
    // Normaliza por área: logos até 2,5:1 usam a altura cheia da faixa; as mais largas
    // encolhem até ter a mesma área de uma 2,5:1 (quadrada não some, larga não domina).
    logoStyle(url) {
      const ratio = this.logoRatios[url];
      const f = ratio ? Math.min(1, Math.sqrt(2.5 / ratio)) : 1;
      return { height: `calc(var(--php-logo-h) * ${f.toFixed(3)})` };
    },
    // primeiraCarga=false no polling: atualiza os números sem piscar a tela
    carregar(primeiraCarga) {
      return PublicHearingService.GetPanel(this.token)
        .then((r) => {
          this.dados = r.data;
          if (primeiraCarga) this.carregarQr();
          this.sincronizarCronometro();
          this.$nextTick(this.medirGrafico);
        })
        .catch(() => {
          if (primeiraCarga) this.dados = null;
        })
        .finally(() => {
          if (primeiraCarga) this.loading = false;
        });
    },
    // Recalcula a contagem regressiva a partir do ends_at do servidor (corrige
    // deriva a cada poll de 20s) e mantém um tick local de 1s entre polls.
    sincronizarCronometro() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
      const ativo = this.dados && this.dados.active_speaker;
      if (!ativo) {
        this.remainingSeconds = 0;
        return;
      }
      // Pausada: contagem congelada, vinda do servidor — sem tick local.
      if (ativo.paused) {
        this.remainingSeconds = Math.max(0, ativo.remaining_seconds || 0);
        return;
      }
      const calcular = () => {
        const restante = Math.round((new Date(ativo.ends_at).getTime() - Date.now()) / 1000);
        this.remainingSeconds = Math.max(0, restante);
      };
      calcular();
      this.countdownTimer = setInterval(calcular, 1000);
    },
    formatarContagem(segundos) {
      const s = Math.max(0, segundos || 0);
      const m = Math.floor(s / 60);
      const r = s % 60;
      return String(m).padStart(2, "0") + ":" + String(r).padStart(2, "0");
    },
    ehOradorAtivo(f) {
      return !!(this.dados && this.dados.active_speaker && this.dados.active_speaker.response_id === f.id);
    },
    selecionarSolicitante(f) {
      if (this.ehOradorAtivo(f)) return;
      this.speakerSelecionado = this.speakerSelecionado === f.id ? null : f.id;
    },
    iniciarCronometro(f) {
      if (!this.dados || this.acaoCronometro) return;
      this.acaoCronometro = true;
      PublicHearingService.StartSpeechTimer(this.dados.event.id, f.id)
        .then(() => {
          this.speakerSelecionado = null;
          return this.carregar(false);
        })
        .catch(() => {})
        .finally(() => {
          this.acaoCronometro = false;
        });
    },
    encerrarCronometro() {
      if (!this.dados || this.acaoCronometro) return;
      this.acaoCronometro = true;
      PublicHearingService.StopSpeechTimer(this.dados.event.id)
        .then(() => this.carregar(false))
        .catch(() => {})
        .finally(() => {
          this.acaoCronometro = false;
        });
    },
    alternarPausaCronometro() {
      if (!this.dados || !this.dados.active_speaker || this.acaoCronometro) return;
      this.acaoCronometro = true;
      const acao = this.dados.active_speaker.paused
        ? PublicHearingService.ResumeSpeechTimer
        : PublicHearingService.PauseSpeechTimer;
      acao.call(PublicHearingService, this.dados.event.id)
        .then(() => this.carregar(false))
        .catch(() => {})
        .finally(() => {
          this.acaoCronometro = false;
        });
    },
    carregarQr() {
      const baseUrl = window.location.origin + window.location.pathname.replace(/\/$/, "");
      PublicHearingService.GetSuggestionsQrCode(this.token, baseUrl)
        .then((r) => {
          if (this.qrUrl) URL.revokeObjectURL(this.qrUrl);
          this.qrUrl = URL.createObjectURL(r.data);
        })
        .catch(() => {});
    },
    // Mede a célula do grid em vez de derivar da altura da janela — sobrevive a
    // mudanças de layout sem recalibrar constantes.
    medirGrafico() {
      const aplicar = () => {
        const el = this.$refs.chartBox;
        if (!el || !el.clientHeight) return;
        const altura = Math.max(140, el.clientHeight - 4);
        if (Math.abs(altura - this.chartH) > 2) this.chartH = altura;
      };
      aplicar();
      // Segunda passada após o grid assentar — sem isso o SVG fica maior que a
      // célula em telas baixas e transborda por cima dos cards de baixo.
      if (typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(aplicar);
      }
    },
    statusLabel(s) {
      return { atendida: "Atendida", nao_atendida: "Não atendida", pendente: "Aguardando" }[s] || s;
    },
    badgeClass(s) {
      return (
        {
          atendida: "php-badge-ok",
          nao_atendida: "php-badge-no",
          pendente: "php-badge-wait"
        }[s] || "php-badge-wait"
      );
    }
  }
};
</script>

<style scoped>
.php-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: #f4f6f9;
  color: #16265c;
  font-family: Cabin, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
  overflow: hidden;
}
.php-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #5a6478;
}

.php-grid {
  /* colunas compartilhadas pela L3 (números) e L4/L5 (gráfico/fila | QR) */
  --php-cols: 2.15fr 1fr;
  height: 100%;
  display: grid;
  grid-template-rows: auto auto auto minmax(0, 1fr) auto;
  gap: 12px;
  padding: 14px 20px 12px;
}

/* Cores utilitárias */
.php-t-navy {
  color: #16265c;
}
.php-t-green {
  color: #1f8a3b;
}
.php-t-red {
  color: #e03127;
}
.php-t-purple {
  color: #5b2d8e;
}
.php-bg-green {
  background: #1f8a3b;
}
.php-bg-blue {
  background: #1b5fa8;
}
.php-bg-purple {
  background: #5b2d8e;
}

/* Card base */
.php-card {
  background: #fff;
  border: 1px solid #e3e8ef;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(22, 38, 92, 0.06);
  padding: 12px 20px;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.php-card-title {
  text-align: center;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 17px;
  letter-spacing: 0.3px;
  margin-bottom: 8px;
}

/* L1 — logos */
.php-logos {
  --php-logo-h: clamp(48px, 9vh, 110px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 56px;
  min-height: var(--php-logo-h);
}
.php-logo-img {
  width: auto;
  max-width: 28vw;
  object-fit: contain;
}
.php-logos-fallback {
  color: #16265c;
  font-weight: 700;
  font-size: 22px;
  text-transform: uppercase;
}

/* L2 — banner */
.php-banner {
  display: flex;
  align-items: center;
  gap: 20px;
  background: #16265c;
  border-radius: 12px;
  padding: 12px 28px;
  color: #fff;
}
.php-skyline {
  width: 190px;
  height: 100px;
  flex-shrink: 0;
}
.php-banner-text {
  flex: 1;
  min-width: 0;
}
.php-banner-title {
  font-size: 40px;
  font-weight: 700;
  text-transform: uppercase;
  margin: 0;
  line-height: 1.05;
  letter-spacing: 0.5px;
}
.php-banner-sub {
  margin: 4px 0 0;
  font-size: 24px;
  font-weight: 700;
  text-transform: uppercase;
  color: #8cc63f;
  line-height: 1.2;
}
.php-banner-date {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-left: 28px;
  border-left: 1px solid rgba(255, 255, 255, 0.28);
  flex-shrink: 0;
}
.php-date-icon {
  font-size: 40px;
  color: #8cc63f;
}
.php-date-label {
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.php-date-value {
  font-size: 31px;
  font-weight: 700;
  line-height: 1.1;
}

/* L3 — cards de números */
.php-row-cards {
  display: grid;
  grid-template-columns: var(--php-cols);
  gap: 12px;
}
/* "Número de contribuições" + "Solicitações de fala" dividem a largura do gráfico */
.php-row-cards-left {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.php-kpi-row {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 0;
}
.php-kpi-row-center {
  justify-content: center;
  gap: 24px;
}
.php-icon-circle {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 30px;
  flex-shrink: 0;
}
.php-kpi-group {
  flex: 1;
  text-align: center;
  min-width: 0;
}
.php-kpi-num {
  font-size: 44px;
  font-weight: 700;
  line-height: 1;
}
.php-kpi-label {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  color: #6b7280;
  letter-spacing: 0.4px;
  margin-top: 3px;
}
.php-kpi-div {
  width: 1px;
  align-self: stretch;
  background: #e3e8ef;
  margin: 6px 0;
}

/* L4/L5 — gráfico e fila à esquerda; QR ocupa a coluna direita nas duas linhas */
.php-row-main {
  display: grid;
  grid-template-columns: var(--php-cols);
  grid-template-rows: minmax(0, 1.35fr) minmax(0, 0.8fr);
  gap: 12px;
  min-height: 0;
}
.php-card-chart {
  grid-column: 1;
  grid-row: 1;
}
.php-card-qr {
  grid-column: 2;
  grid-row: 1 / span 2;
}
.php-card-list {
  grid-column: 1;
  grid-row: 2;
}
.php-chart-box {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.php-card-qr {
  background: #f1f7f1;
  border-color: #dbe9db;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 10px 16px;
}
.php-qr-title {
  font-size: 21px;
  font-weight: 700;
  text-transform: uppercase;
  color: #1f8a3b;
}
.php-qr-sub {
  font-size: 15px;
  font-weight: 700;
  text-transform: uppercase;
  color: #16265c;
  line-height: 1.25;
  margin-top: 2px;
}
/* Área livre do card: container de tamanho, para o QR ser o maior quadrado que
   cabe nela (100cqmin = menor entre largura e altura) — só CSS, já no 1º paint. */
.php-qr-area {
  container-type: size;
  flex: 1;
  min-height: 0;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin: 6px 0;
}
.php-qr-frame {
  background: #fff;
  border: 1px solid #dbe9db;
  border-radius: 10px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}
.php-qr-img {
  display: block;
  /* 18px = padding (8) + borda (1) da moldura, nos dois lados */
  width: calc(100cqmin - 18px);
  height: calc(100cqmin - 18px);
  max-width: none;
  /* módulos nítidos ao ampliar acima do tamanho nativo do PNG */
  image-rendering: pixelated;
}
.php-qr-placeholder {
  font-size: 60px;
  color: #ccc;
}
.php-qr-btn {
  background: #1f8a3b;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 7px 22px;
  letter-spacing: 0.4px;
}
.php-qr-caption {
  font-size: 12px;
  font-weight: 500;
  color: #16265c;
  text-transform: uppercase;
  margin-top: 6px;
}

/* L5 — fila de fala */
.php-card-list {
  min-height: 0;
}
.php-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding-right: 4px;
}
.php-empty {
  color: #8a94a6;
  font-size: 14px;
  padding: 6px 0;
  text-align: center;
}
.php-item {
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #eef1f5;
  padding: 7px 0;
}
.php-item:last-child {
  border-bottom: none;
}
.php-item-pos {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #eef2f8;
  color: #16265c;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.php-item-body {
  flex: 1;
  min-width: 0;
}
.php-item-name {
  font-size: 16px;
  font-weight: 700;
  color: #16265c;
}
.php-item-meta {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}
.php-badge {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 3px 12px;
  white-space: nowrap;
  flex-shrink: 0;
}
.php-badge-ok {
  background: #e6f4ea;
  color: #1f8a3b;
}
.php-badge-no {
  background: #fdeceb;
  color: #e03127;
}
.php-badge-wait {
  background: #fff4e5;
  color: #b26a00;
}
.php-badge-live {
  background: #e6f4ea;
  color: #1f8a3b;
}
.php-badge-spoken {
  background: #e8edf5;
  color: #16265c;
}
/* Já falou: esmaecido na fila, mas continua selecionável (pode falar de novo) */
.php-item-spoken .php-item-body {
  opacity: 0.6;
}

/* Fila de fala — seleção e controle do técnico (só logado) */
.php-item-clickable {
  cursor: pointer;
  border-radius: 8px;
}
.php-item-clickable:hover {
  background: #f7f8fb;
}
.php-item-selected {
  background: #f3eefa;
  border-radius: 8px;
}
.php-item-active {
  background: #eaf7ed;
  border-radius: 8px;
}
.php-btn-iniciar {
  border: none;
  background: #5b2d8e;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  border-radius: 999px;
  padding: 5px 14px;
  white-space: nowrap;
  flex-shrink: 0;
}
.php-btn-iniciar:disabled {
  opacity: 0.6;
}

/* Card "Tempo de fala" — estado com orador ativo (nome + contagem cabem na altura
   do círculo do ícone, igual ao estado normal) */
.php-speaker-name {
  font-size: 14px;
  font-weight: 700;
  color: #16265c;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}
.php-countdown {
  font-variant-numeric: tabular-nums;
}
.php-countdown-ended {
  color: #e03127;
}
.php-countdown-paused {
  opacity: 0.5;
}
.php-timer-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
}
.php-btn-pausar {
  border: 1px solid #e0a800;
  background: #e0a800;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  border-radius: 999px;
  padding: 4px 14px;
  white-space: nowrap;
}
.php-btn-pausar:hover:not(:disabled) {
  background: #b88a00;
  border-color: #b88a00;
}
.php-btn-pausar:disabled {
  opacity: 0.6;
}
.php-btn-encerrar {
  border: 1px solid #e03127;
  background: #e03127;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  border-radius: 999px;
  padding: 4px 14px;
  white-space: nowrap;
  flex-shrink: 0;
}
.php-btn-encerrar:hover:not(:disabled) {
  background: #b8261e;
  border-color: #b8261e;
}
.php-btn-encerrar:disabled {
  opacity: 0.6;
}

/* L6 — faixa final */
.php-footer {
  background: #eef4ef;
  border: 1px solid #dbe9db;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 11px;
  font-size: 18px;
  font-weight: 700;
  color: #16265c;
}
.php-footer-icon {
  font-size: 24px;
  color: #1f8a3b;
}
.php-footer-people {
  font-size: 24px;
  color: #16265c;
}

/* Telas de pouca altura (notebook) */
@media (max-height: 820px) {
  .php-grid {
    gap: 8px;
    padding: 10px 14px 8px;
  }
  .php-logos {
    gap: 36px;
  }
  .php-banner {
    padding: 8px 20px;
  }
  .php-skyline {
    width: 140px;
    height: 74px;
  }
  .php-banner-title {
    font-size: 29px;
  }
  .php-banner-sub {
    font-size: 17px;
  }
  .php-date-value {
    font-size: 24px;
  }
  .php-date-icon {
    font-size: 30px;
  }
  .php-icon-circle {
    width: 52px;
    height: 52px;
    font-size: 23px;
  }
  .php-kpi-num {
    font-size: 33px;
  }
  .php-card {
    padding: 9px 14px;
  }
  .php-card-title {
    font-size: 14px;
    margin-bottom: 5px;
  }
  .php-footer {
    padding: 8px;
    font-size: 15px;
  }
  .php-qr-title {
    font-size: 17px;
  }
  .php-qr-sub {
    font-size: 13px;
  }
  .php-qr-area {
    margin: 4px 0;
  }
}

/* Telas muito baixas (notebook com escala 125–150%, projetor 1024×768): os textos
   secundários do card do QR saem para o QR não ser espremido até sumir. */
@media (max-height: 700px) {
  .php-qr-sub,
  .php-qr-caption {
    display: none;
  }
  .php-qr-btn {
    font-size: 11px;
    padding: 4px 16px;
  }
}

/* Telas estreitas */
@media (max-width: 1250px) {
  .php-banner-title {
    font-size: 28px;
  }
  .php-banner-sub {
    font-size: 17px;
  }
  .php-grid {
    --php-cols: 1.7fr 1fr;
  }
  .php-kpi-num {
    font-size: 34px;
  }
}
</style>
